import ReactDOM from 'react-dom';
import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { CardContent, Collapse, ToggleButton, ToggleButtonGroup } from "@mui/material";
import moment from "moment";
import toast from "react-hot-toast";
import * as htmlToImage from 'html-to-image';

import CustomDropDownToggle from "../../../common/CustomDropDownToggle";
import CustomActionButton from "../../../common/CustomActionButton";
import CustomButton from "../../../common/CustomButton";
import CustomSliderModalV2 from "../../../common/CustomSliderModalV2";
import CustomSelect from '../../../common/CustomSelect';

import { SendEmail } from "../../../redux/brg/actions";
import { delay } from "../../../utils";
import useSVG from "../../../hooks/useSVG";

function EmailContent(props) {
    const { patient, Brg, filtered = false, suggestedPrograms } = props;
    const emailTemplates = props?.EmailTemplate?.list;
    const [isOpen, setIsOpen] = useState(false);
    const [eventID, setEventID] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [emailVersion, setEmailVersion] = useState("latest");
    const [displayFormat, setDisplayFormat] = useState("email");
    const [generatedEmails, setGeneratedEmails] = useState([]);
    const [htmlPreview, setHtmlPreview] = useState("");
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [etOptions, setEtOptions] = useState([]);
    const { UseProgramSVG } = useSVG();

    const email = generatedEmails?.[emailVersion == "latest" ? 0 : 1] || null;


    useEffect(() => {
        if (emailTemplates?.length > 0) {
            const defaultET = emailTemplates?.filter((et) => (et?.tag == "post-discharge" && et?.is_default == 1))?.[0]
            setEmailTemplate(defaultET || null);

            const tempETO = [defaultET, ...emailTemplates.filter((et) => (et?.tag == "post-discharge" && et?.is_default == 0))];
            setEtOptions(tempETO.map((et) => {
                return {
                    value: et?.id,
                    label: et?.name.toUpperCase(),
                }
            }));
        }

    }, [emailTemplates?.length]);

    useEffect(() => {

        if (patient?.generatedEmails.length > 0) {
            let emails = patient?.generatedEmails?.filter((ge) => ge?.filtered == (filtered ? 1 : 0))
            emails = emails.reverse();

            setGeneratedEmails(emails);
            props.setSelectedEmail(emails?.[0]);

        }

    }, [patient?.id, patient?.generatedEmails?.length]);

    useEffect(() => {
        props.setSelectedEmail(generatedEmails?.[emailVersion == "latest" ? 0 : 1] || null);
    }, [emailVersion]);


    const arrange = (emailContent) => {
        const sp = emailContent?.["suggestions"] || emailContent?.["suggested_programs"];

        let content = "";
        content += `Subject: ${emailContent?.email_subject}\n\n`;
        content += `Preview Text: ${emailContent?.email_preview_text}\n\n`;
        content += `${emailContent?.headline_1}\n\n`;
        content += `${emailContent?.subtext_1}\n\n`;
        content += `${emailContent?.headline_2}\n\n`;
        content += `${emailContent?.subtext_2}\n\n`;
        content += `${emailContent?.headline_3}\n\n`;
        for (let i = 0; i < sp.length; i++) {
            content += `    ${sp[i]?.program_name}\n\n`;
            content += `    ${sp[i]?.program_description}\n\n`;
        }
        content += `${emailContent?.subtext_3}\n\n`;
        content += `${emailContent?.sign_off}\n\n`;

        return content;
    }

    const extractHTML = (reactElement) => {
        // Create a temporary container
        const container = document.createElement('div');

        // Render the React element into the container
        ReactDOM.render(reactElement, container);

        // Extract the inner HTML of the container
        const htmlString = container.innerHTML;

        // Cleanup: unmount the component and remove the container
        ReactDOM.unmountComponentAtNode(container);
        container.remove();

        return htmlString;
    }

    const arrangeHTMLContent = (htmlTemplate, patient, generatedEmail, eventID, sp) => {
        let htmlContent = htmlTemplate;
        const emailContent = JSON.parse(email?.email_content);
        const link = (process.env.REACT_APP_ENVIRONMENT === "PROD" ? process.env.REACT_APP_BASE_API_URL : process.env.REACT_APP_BASE_API_URL_DEV);

        htmlContent = htmlContent.replace("{{patient_name}}", (patient?.first_name));
        htmlContent = htmlContent.replace("{{email_preview_text}}", emailContent?.email_preview_text);
        htmlContent = htmlContent.replace("{{headline_1}}", emailContent?.headline_1);
        htmlContent = htmlContent.replace("{{subtext_1}}", emailContent?.subtext_1);
        htmlContent = htmlContent.replace("{{headline_2}}", emailContent?.headline_2);
        htmlContent = htmlContent.replace("{{subtext_2}}", emailContent?.subtext_2);
        htmlContent = htmlContent.replace("{{headline_3}}", emailContent?.headline_3);

        let svg = null, gradient = null, src = null;
        for (let i = 0; i < emailContent?.suggested_programs.length; i++) {
            htmlContent = htmlContent.replace(`{{program_name${i + 1}}}`, emailContent?.suggested_programs[i]?.program_name);
            htmlContent = htmlContent.replace(`{{program_description${i + 1}}}`, emailContent?.suggested_programs[i]?.program_description);
            gradient = sp[i]?.gradient?.split("-")
            svg = <div className="program-img-wrapper" style={{ backgroundImage: `linear-gradient(to right, ${gradient?.[0]}, ${gradient?.[1]})`, borderRadius: "50%", padding: "12px" }}>
                <img src={`${link}images/program-icons/${sp[i]?.svg?.split(".")[0]}.png`} alt="" />
            </div>
            htmlContent = htmlContent.replace(`{{program_image_${i + 1}}}`, extractHTML(svg));
        }

        htmlContent = htmlContent.replace("{{subtext_3}}", emailContent?.subtext_3);
        htmlContent = htmlContent.replace("{{sign_off}}", emailContent?.sign_off);
        htmlContent = htmlContent.replace("{{signup_link}}", `${"https://indimoves.com/brg/"}?type=brgpatient&id=${generatedEmail?.id}&event_id=${eventID}&env=${process.env.REACT_APP_ENVIRONMENT}`);
        htmlContent = htmlContent.replace("{{signup_link2}}", `${"https://indimoves.com/brg/"}?type=brgpatient&id=${generatedEmail?.id}&event_id=${eventID}&env=${process.env.REACT_APP_ENVIRONMENT}`);

        return htmlContent;
    }

    const handlePreviewEmail = async (et = null) => {
        et = et || emailTemplate;
        const toastID = toast.loading("Generating Email Preview...");
        const tempEventID = new Date().getTime();

        setEventID(eID => tempEventID);
        setHtmlPreview(arrangeHTMLContent(et?.html_content, patient, email, tempEventID, suggestedPrograms));

        toast.dismiss(toastID);
        setShowModal(true);
    }

    const handleSendEmail = async () => {
        const toastID = toast.loading("Sending Email...");
        const genEmail = generatedEmails?.[emailVersion == "latest" ? 0 : 1] || null;
        let subject = emailTemplate?.email_subject;
        if (emailTemplate?.email_subject == "ai_generated") {
            let ec = JSON.parse(email?.email_content);
            subject = ec?.email_subject
        } else {
            subject = subject.replace("{{patient_name}}", patient?.first_name);
        }

        const res = await props.SendEmail(patient, genEmail, htmlPreview, subject, eventID, emailTemplate);

        if (res) {
            toast.success("Email Sent Successfully", {
                id: toastID,
            });
            delay(() => {
                setShowModal(false);
            }, 1500)
        } else {
            toast.error("Failed to Send Email", {
                id: toastID,
            });
        }
    }

    let parsedEmail = "";
    try {
        parsedEmail = arrange(JSON.parse(email?.email_content));
    } catch (error) {
        parsedEmail = "ERROR PARSING EMAIL CONTENT";
    }


    return (
        <>
            <div className="brg-pp brg-pp-email-prompt">
                <CustomDropDownToggle isOpen={isOpen} toggle={() => {
                    setIsOpen(!isOpen);
                }} />

                <h5>
                    {`${filtered ? "FILTERED EMAIL CONTENT" : "EMAIL CONTENT"}`}
                </h5>

                <Collapse in={isOpen} timeout="auto" unmountOnExit>
                    <CardContent>
                        <ToggleButtonGroup
                            className="brg-pp-em-toggle-group"
                            color="info"
                            value={emailVersion}
                            exclusive
                            onChange={(e, val) => {
                                setEmailVersion(val);
                            }}
                            aria-label="Platform"
                        >
                            <ToggleButton value="latest">LATEST</ToggleButton>
                            <ToggleButton value="previous">PREVIOUS</ToggleButton>
                        </ToggleButtonGroup>
                        <h6>
                            Date Generated: <em style={{ fontSize: "0.8rem" }}>{moment(email?.created_at).format("LLL")}</em>
                        </h6>
                        <div className="brg-pp-em-group">
                            {
                                displayFormat == "email" && <pre contentEditable={true} onInput={(e) => {
                                }}>
                                    {email ? parsedEmail : "NONE"}
                                </pre>
                            }

                            {
                                displayFormat == "ARRANGE" && <div id="brg-pp-ep-arrange-format">

                                </div>
                            }

                        </div>
                    </CardContent>


                </Collapse>

                {filtered && <div id="generate-email-wrapper" className="flex-r">
                    {
                        patient?.email
                            ? <CustomButton size="small" disabled={!email} onClick={() => { handlePreviewEmail() }}>
                                SEND EMAIL
                            </CustomButton>
                            : <em>No Email Address</em>

                    }

                </div>}
            </div>

            {
                (emailTemplate?.html_content && filtered) &&
                <CustomSliderModalV2 show={showModal} setShow={setShowModal} title={"Email Preview"}>
                    <div id="ep-cs-template">
                        <CustomSelect placeholder={"Template"} options={etOptions} onChange={(e) => {
                            const et = emailTemplates?.filter((et) => et?.id == e.target.value)?.[0];
                            setEmailTemplate(et);
                            handlePreviewEmail(et);
                        }} />
                    </div>

                    <div className="preview" dangerouslySetInnerHTML={{ __html: htmlPreview }} />
                    <div className="flex-r pt-5 pb-5">
                        <CustomActionButton disabled={!email?.created_at} action={handleSendEmail}>
                            SEND EMAIL
                        </CustomActionButton>
                    </div>
                </CustomSliderModalV2>
            }

        </>
    );
}

const mapStateToProps = (state) => ({
    Brg: state.Brg,
    EmailTemplate: state.EmailTemplate,
})

export default connect(mapStateToProps, {
    SendEmail,
})(EmailContent);
